import React from 'react';
import { PencilIcon } from '@heroicons/react/24/outline';
import { WorkOrder } from '../../services/manufacturing.service';
import { Modal } from '../ui/Modal';
import {
  DetailCard,
  DetailItem,
  FormActions,
  badgeClass,
  formatMoney,
  formatNumber,
  primaryButtonClass,
  secondaryButtonClass,
} from '../ui/FormControls';
import { WORK_ORDER_STATUS_LABELS } from './WorkOrderForm';

interface ViewWorkOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  workOrder: WorkOrder | null;
  onEdit?: (workOrder: WorkOrder) => void;
}

const STATUS_BADGES: Record<WorkOrder['status'], { color: string; bar: string }> = {
  draft: { color: 'text-gray-700 bg-gray-50 ring-gray-500/20', bar: 'bg-gray-400' },
  planned: { color: 'text-blue-700 bg-blue-50 ring-blue-600/20', bar: 'bg-blue-500' },
  in_progress: { color: 'text-yellow-800 bg-yellow-50 ring-yellow-600/20', bar: 'bg-yellow-500' },
  completed: { color: 'text-green-700 bg-green-50 ring-green-600/20', bar: 'bg-green-500' },
  cancelled: { color: 'text-red-700 bg-red-50 ring-red-600/20', bar: 'bg-red-400' },
  on_hold: { color: 'text-orange-700 bg-orange-50 ring-orange-600/20', bar: 'bg-orange-500' },
};

const PRIORITY_BADGES: Record<WorkOrder['priority'], { label: string; color: string }> = {
  urgent: { label: 'Urgent', color: 'text-red-700 bg-red-50 ring-red-600/20' },
  high: { label: 'High', color: 'text-orange-700 bg-orange-50 ring-orange-600/20' },
  medium: { label: 'Medium', color: 'text-yellow-800 bg-yellow-50 ring-yellow-600/20' },
  low: { label: 'Low', color: 'text-green-700 bg-green-50 ring-green-600/20' },
};

const FALLBACK_BADGE = 'text-gray-700 bg-gray-50 ring-gray-500/20';

const formatDate = (value?: string) =>
  value
    ? new Date(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';

const formatDateTime = (value?: string) =>
  value
    ? new Date(value).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

const ViewWorkOrderModal: React.FC<ViewWorkOrderModalProps> = ({
  isOpen,
  onClose,
  workOrder,
  onEdit,
}) => {
  if (!workOrder) return null;

  const status = STATUS_BADGES[workOrder.status];
  const priority = PRIORITY_BADGES[workOrder.priority];
  const plannedQuantity = Number(workOrder.plannedQuantity) || 0;
  const completedQuantity = Number(workOrder.completedQuantity) || 0;
  const progress =
    plannedQuantity > 0 ? Math.min(100, Math.max(0, (completedQuantity / plannedQuantity) * 100)) : 0;
  const unit = workOrder.unit ? ` ${workOrder.unit}` : '';
  const materials: any[] = (workOrder as any).materialConsumptions || [];
  const canEdit = ['draft', 'planned', 'on_hold'].includes(workOrder.status);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Work Order Details" size="xl">
      <div className="space-y-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p>
              <span className="rounded bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-700">
                {workOrder.workOrderNumber}
              </span>
            </p>
            <h4 className="mt-2 text-lg font-semibold text-gray-900">
              {workOrder.product?.name || 'N/A'}
            </h4>
            {workOrder.product?.sku && (
              <p className="text-sm text-gray-500">{workOrder.product.sku}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className={`${badgeClass} ${priority?.color || FALLBACK_BADGE}`}>
              {priority?.label || workOrder.priority} priority
            </span>
            <span className={`${badgeClass} ${status?.color || FALLBACK_BADGE}`}>
              {WORK_ORDER_STATUS_LABELS[workOrder.status] || workOrder.status}
            </span>
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
          <div className="flex items-baseline justify-between">
            <p className="text-sm text-gray-500">Production progress</p>
            <p className="text-sm font-medium text-gray-900 tabular-nums">{Math.round(progress)}%</p>
          </div>
          <div className="mt-2 h-2 w-full rounded-full bg-gray-200">
            <div
              className={`h-2 rounded-full ${status?.bar || 'bg-gray-400'}`}
              style={{ width: `${progress}%` }}
            />
          </div>
          <dl className="mt-4 grid grid-cols-3 gap-4 text-sm">
            <div>
              <dt className="text-xs text-gray-500">Planned</dt>
              <dd className="font-semibold text-gray-900 tabular-nums">
                {formatNumber(plannedQuantity)}
                {unit}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">Completed</dt>
              <dd className="font-semibold text-gray-900 tabular-nums">
                {formatNumber(completedQuantity)}
                {unit}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500">Rejected</dt>
              <dd
                className={`font-semibold tabular-nums ${
                  Number(workOrder.rejectedQuantity) > 0 ? 'text-red-600' : 'text-gray-900'
                }`}
              >
                {formatNumber(Number(workOrder.rejectedQuantity))}
                {unit}
              </dd>
            </div>
          </dl>
        </div>

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          <DetailCard title="Schedule">
            <DetailItem label="Planned start">{formatDate(workOrder.plannedStartDate)}</DetailItem>
            <DetailItem label="Planned end">{formatDate(workOrder.plannedEndDate)}</DetailItem>
            <DetailItem label="Actual start">{formatDateTime(workOrder.actualStartDate)}</DetailItem>
            <DetailItem label="Actual end">{formatDateTime(workOrder.actualEndDate)}</DetailItem>
          </DetailCard>

          <DetailCard title="Cost and effort">
            <DetailItem label="Estimated cost">{formatMoney(Number(workOrder.estimatedCost))}</DetailItem>
            <DetailItem label="Actual cost">{formatMoney(Number(workOrder.actualCost))}</DetailItem>
            <DetailItem label="Labor hours">{formatNumber(Number(workOrder.laborHours))} hrs</DetailItem>
            <DetailItem label="Machine hours">{formatNumber(Number(workOrder.machineHours))} hrs</DetailItem>
          </DetailCard>
        </div>

        <DetailCard title="References" columns={4}>
          <DetailItem label="Production line">{workOrder.productionLine || '—'}</DetailItem>
          <DetailItem label="Batch number">{workOrder.batchNumber || '—'}</DetailItem>
          <DetailItem label="Bill of materials">{workOrder.bomHeaderNumber || '—'}</DetailItem>
          <DetailItem label="Sales order">{workOrder.salesOrderReference || '—'}</DetailItem>
          <DetailItem label="Customer reference">{workOrder.customerReference || '—'}</DetailItem>
        </DetailCard>

        {materials.length > 0 && (
          <section className="overflow-hidden rounded-lg border border-gray-200">
            <h4 className="border-b border-gray-200 bg-gray-50 px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
              Materials
            </h4>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead>
                  <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    <th className="px-4 py-2">Material</th>
                    <th className="px-4 py-2 text-right">Planned</th>
                    <th className="px-4 py-2 text-right">Used</th>
                    <th className="px-4 py-2 text-right">Unit cost</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {materials.map((material) => (
                    <tr key={material.id}>
                      <td className="px-4 py-2">
                        <span className="font-medium text-gray-900">{material.material?.name || 'N/A'}</span>
                        {material.material?.sku && (
                          <span className="ml-2 text-gray-500">{material.material.sku}</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums text-gray-700">
                        {formatNumber(Number(material.plannedQuantity))}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums text-gray-700">
                        {formatNumber(Number(material.actualQuantity))}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums text-gray-700">
                        {formatMoney(Number(material.unitCost))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {workOrder.notes && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Notes</p>
            <p className="mt-1 whitespace-pre-wrap rounded-md bg-gray-50 p-3 text-sm text-gray-700">
              {workOrder.notes}
            </p>
          </div>
        )}

        {workOrder.qualityNotes && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Quality notes</p>
            <p className="mt-1 whitespace-pre-wrap rounded-md border-l-4 border-yellow-400 bg-yellow-50 p-3 text-sm text-gray-700">
              {workOrder.qualityNotes}
            </p>
          </div>
        )}

        <p className="text-xs text-gray-500">
          Created {formatDateTime(workOrder.createdAt)} · Last updated {formatDateTime(workOrder.updatedAt)}
          {workOrder.approvedAt && ` · Approved ${formatDateTime(workOrder.approvedAt)}`}
        </p>
      </div>

      <FormActions>
        <button type="button" onClick={onClose} className={secondaryButtonClass}>
          Close
        </button>
        {onEdit && canEdit && (
          <button type="button" onClick={() => onEdit(workOrder)} className={primaryButtonClass}>
            <PencilIcon className="mr-2 h-4 w-4" />
            Edit Work Order
          </button>
        )}
      </FormActions>
    </Modal>
  );
};

export default ViewWorkOrderModal;
